import React, { useState, useRef, useEffect } from 'react';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { sendHelpMessage } from '../services/api';
import { HelpCircle, X, Send, Sparkles, Loader2 } from 'lucide-react';

export default function AIHelpButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your ThriveLonger.ai assistant. Ask me anything about the platform's features, how to navigate, or what each section does. How can I help?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const resp = await sendHelpMessage(text);
      setMessages(prev => [...prev, { role: 'assistant', content: resp.content }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Try again or visit our Help section at /info for guides." }]);
    }
    setLoading(false);
  };

  const QUICK_QUESTIONS = [
    'What features are available?',
    'How do health scores work?',
    'How do I join a challenge?',
    'What membership tiers exist?',
  ];

  // Don't show if not logged in
  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        data-testid="ai-help-btn"
        onClick={() => setOpen(!open)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          width: 56, height: 56, borderRadius: 28,
          backgroundColor: open ? COLORS.textPrimary : COLORS.primary,
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(107,142,35,0.35)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {open ? <X size={24} color="#FFF" /> : <HelpCircle size={26} color="#FFF" />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div data-testid="ai-help-panel" style={{
          position: 'fixed', bottom: 92, right: 24, zIndex: 999,
          width: 380, maxWidth: 'calc(100vw - 48px)', height: 520, maxHeight: 'calc(100vh - 120px)',
          backgroundColor: '#FFF', borderRadius: 20,
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'fadeInUp 0.3s ease-out',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', background: `linear-gradient(135deg, ${COLORS.primary}, #4a6e18)`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={20} color="#FFF" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#FFF' }}>AI Help Assistant</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Ask about features & navigation</div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{
            flex: 1, overflowY: 'auto', padding: '16px 16px 8px',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div data-testid={`help-msg-${i}`} style={{
                  maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
                  backgroundColor: msg.role === 'user' ? COLORS.primary : COLORS.surfaceSecondary,
                  color: msg.role === 'user' ? '#FFF' : COLORS.textPrimary,
                  fontSize: 14, lineHeight: 1.5,
                  borderBottomRightRadius: msg.role === 'user' ? 4 : 14,
                  borderBottomLeftRadius: msg.role === 'assistant' ? 4 : 14,
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: COLORS.surfaceSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Sparkles size={14} color={COLORS.primary} />
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <span className="help-typing-dot" style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: COLORS.primary, opacity: 0.4, animationDelay: '0s' }} />
                  <span className="help-typing-dot" style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: COLORS.primary, opacity: 0.4, animationDelay: '0.2s' }} />
                  <span className="help-typing-dot" style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: COLORS.primary, opacity: 0.4, animationDelay: '0.4s' }} />
                </div>
              </div>
            )}
          </div>

          {/* Quick Questions (only when few messages) */}
          {messages.length <= 2 && !loading && (
            <div style={{ padding: '0 16px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {QUICK_QUESTIONS.map((q, i) => (
                <button key={i} data-testid={`quick-q-${i}`} onClick={() => { setInput(q); setTimeout(() => { setInput(q); handleSendDirect(q); }, 50); }}
                  style={{
                    padding: '6px 12px', borderRadius: 9999, border: `1px solid ${COLORS.primary}30`,
                    backgroundColor: `${COLORS.primary}08`, color: COLORS.primary,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: `1px solid ${COLORS.border}40`,
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <input
              ref={inputRef}
              data-testid="help-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about features..."
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 12,
                border: `1px solid ${COLORS.border}`, fontSize: 14,
                color: COLORS.textPrimary, outline: 'none',
              }}
            />
            <button data-testid="help-send" onClick={handleSend} disabled={loading || !input.trim()} style={{
              width: 40, height: 40, borderRadius: 12, border: 'none',
              backgroundColor: input.trim() ? COLORS.primary : COLORS.surfaceSecondary,
              cursor: input.trim() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}>
              <Send size={18} color={input.trim() ? '#FFF' : COLORS.textSecondary} />
            </button>
          </div>
        </div>
      )}
    </>
  );

  async function handleSendDirect(text) {
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const resp = await sendHelpMessage(text);
      setMessages(prev => [...prev, { role: 'assistant', content: resp.content }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Try again or visit /info for guides." }]);
    }
    setLoading(false);
  }
}
